import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Badge, Card, type BadgeProps } from "@pericles/framework-frontend";

import {
  analyzeSpecSource,
  attachDocumentToSpecSource,
  createSpecSource,
  deleteSpecSection,
  fetchSpecSections,
  fetchSpecSources,
} from "../api/specsClient";
import { fetchProjectDocuments } from "../api/analysisClient";
import { uploadProjectDocument } from "../api/projectDocumentsClient";
import type { ProjectDocument } from "../types/documents";
import type { SpecSection, SpecSource } from "../types/specs";

import { InlineAlert } from "./InlineAlert";
import { AiReviewStatusBadge } from "./RequirementReviewStatusBadge";

interface ProjectSpecificationPanelProps {
  projectId: string;
  refreshKey?: number;
}

interface SpecSourceFormState {
  name: string;
  sourceType: string;
  description: string;
  uri: string;
}

function getBadgeConfig(
  sourceId: string,
  analyzingId: string | null,
  sectionsBySource: Map<string, SpecSection[]>,
): { label: string; variant: BadgeProps["variant"]; description?: string } {
  if (analyzingId === sourceId) {
    return { label: "Analyzing", variant: "warning", description: "Generating sections" };
  }

  const sectionCount = sectionsBySource.get(sourceId)?.length ?? 0;

  if (sectionCount > 0) {
    return { label: "Sections ready", variant: "success", description: `${sectionCount} sections generated` };
  }

  return { label: "Pending", variant: "default", description: "No analysis yet" };
}

function normalizeSpecSourceId(section: SpecSection): string | null {
  if (section.specSourceId) return section.specSourceId;
  if (typeof section.specSource === "string") return section.specSource;
  if (section.specSource == null) return null;
  return String(section.specSource);
}

export function ProjectSpecificationPanel({ projectId, refreshKey }: ProjectSpecificationPanelProps): JSX.Element {
  const [sources, setSources] = useState<SpecSource[]>([]);
  const [sections, setSections] = useState<SpecSection[]>([]);
  const [loadingSources, setLoadingSources] = useState<boolean>(false);
  const [loadingSections, setLoadingSections] = useState<boolean>(false);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<SpecSourceFormState>({
    name: "",
    sourceType: "document",
    description: "",
    uri: "",
  });
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [uploadingSourceId, setUploadingSourceId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);

  const sectionsBySource = useMemo(() => {
    const map = new Map<string, SpecSection[]>();

    for (const section of sections) {
      const sourceId = normalizeSpecSourceId(section);
      if (!sourceId) continue;

      const current = map.get(sourceId) ?? [];
      current.push(section);
      map.set(sourceId, current);
    }

    for (const [key, value] of map.entries()) {
      value.sort((a, b) => {
        const sequenceA = a.sequence ?? 0;
        const sequenceB = b.sequence ?? 0;
        return sequenceA - sequenceB;
      });
    }

    return map;
  }, [sections]);

  const sectionGroups = useMemo(() => {
    if (sources.length > 0) {
      return sources.map((source) => ({
        id: source.id,
        label: source.name,
        sections: sectionsBySource.get(source.id) ?? [],
      }));
    }

    return Array.from(sectionsBySource.entries()).map(([sourceId, groupedSections]) => ({
      id: sourceId,
      label: sourceId,
      sections: groupedSections,
    }));
  }, [sectionsBySource, sources]);

  useEffect(() => {
    void loadSources();
    void loadSections();
    void loadDocuments();
  }, [projectId, refreshKey]);

  const loadSources = async () => {
    if (!projectId) return;

    setLoadingSources(true);
    setError(null);

    try {
      const result = await fetchSpecSources(projectId);
      setSources(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load spec sources";
      setError(message);
    } finally {
      setLoadingSources(false);
    }
  };

  const loadDocuments = async () => {
    if (!projectId) return;

    setLoadingDocuments(true);
    setError(null);

    try {
      const result = await fetchProjectDocuments(projectId);
      setDocuments(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load documents";
      setError(message);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadSections = async () => {
    if (!projectId) return;

    setLoadingSections(true);
    setError(null);

    try {
      const result = await fetchSpecSections(projectId);
      setSections(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load spec sections";
      setError(message);
    } finally {
      setLoadingSections(false);
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!sectionId) return;

    const confirmed = window.confirm("Delete this section? This action cannot be undone.");
    if (!confirmed) return;

    setDeletingSectionId(sectionId);
    setError(null);

    try {
      await deleteSpecSection(sectionId);
      setSections((current) => current.filter((section) => section.id !== sectionId));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete section";
      setError(message);
    } finally {
      setDeletingSectionId(null);
    }
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!projectId || !formState.name.trim() || !formState.sourceType.trim()) return;

    setIsCreating(true);
    setError(null);

    try {
      await createSpecSource({
        projectId,
        name: formState.name.trim(),
        sourceType: formState.sourceType.trim(),
        description: formState.description.trim() || undefined,
        uri: formState.uri.trim() || undefined,
      });

      setFormState({ name: "", sourceType: formState.sourceType, description: "", uri: "" });
      await loadSources();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create spec source";
      setError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAnalyze = async (specSourceId: string) => {
    setAnalyzingId(specSourceId);
    setError(null);

    try {
      await analyzeSpecSource(specSourceId);
      await loadSources();
      await loadSections();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to analyze spec source";
      setError(message);
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleUpload = async (specSourceId: string, file: File | null) => {
    if (!file) return;
    if (!projectId) {
      setUploadError("Upload failed: project is missing.");
      return;
    }

    setUploadingSourceId(specSourceId);
    setUploadError(null);

    try {
      const document = await uploadProjectDocument(projectId, specSourceId, file);
      const specSource = await attachDocumentToSpecSource(specSourceId, document.id);

      setDocuments((current) => {
        const withoutDoc = current.filter((existing) => existing.id !== document.id);
        return [...withoutDoc, document];
      });

      setSources((current) =>
        current.map((source) => (source.id === specSource.id ? { ...source, ...specSource } : source)),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to upload document";
      const lowerMessage = message.toLowerCase();
      const friendlyMessage = lowerMessage.includes("project")
        ? "Upload failed: project is missing."
        : lowerMessage.includes("specsource") || lowerMessage.includes("spec source")
          ? "Upload failed: specification source is missing."
          : `Upload failed: ${message}`;
      // eslint-disable-next-line no-console
      console.error("Document upload failed", err);
      setUploadError(friendlyMessage);
    } finally {
      setUploadingSourceId(null);
    }
  };

  const documentsById = useMemo(() => {
    const map = new Map<string, ProjectDocument>();
    documents.forEach((doc) => map.set(doc.id, doc));
    return map;
  }, [documents]);

  const documentsBySource = useMemo(() => {
    const map = new Map<string, ProjectDocument[]>();

    documents.forEach((doc) => {
      if (!doc.specSourceId) return;
      const current = map.get(doc.specSourceId) ?? [];
      current.push(doc);
      map.set(doc.specSourceId, current);
    });

    return map;
  }, [documents]);

  return (
    <div className="flex flex-col gap-4">
      <Card title="Add specification source">
        <form className="flex flex-col gap-4" onSubmit={handleCreate}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              <span>Name</span>
              <input
                type="text"
                className="input"
                placeholder="Functional scope"
                value={formState.name}
                onChange={(e: any) => setFormState({ ...formState, name: e.target.value })}
                required
              />
            </label>

            <label className="flex flex-col gap-1 text-sm text-slate-700">
              <span>Source type</span>
              <input
                type="text"
                className="input"
                placeholder="document, url, notes"
                value={formState.sourceType}
                onChange={(e: any) => setFormState({ ...formState, sourceType: e.target.value })}
                required
              />
            </label>

            <label className="flex flex-col gap-1 text-sm text-slate-700 md:col-span-2">
              <span>Description</span>
              <textarea
                className="input"
                rows={2}
                placeholder="Explain what this source covers"
                value={formState.description}
                onChange={(e: any) => setFormState({ ...formState, description: e.target.value })}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm text-slate-700 md:col-span-2">
              <span>URI (optional)</span>
              <input
                type="text"
                className="input"
                placeholder="Link to the source material"
                value={formState.uri}
                onChange={(e: any) => setFormState({ ...formState, uri: e.target.value })}
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" className="button button-primary" disabled={isCreating}>
              {isCreating ? "Creating…" : "Create source"}
            </button>
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setFormState({ name: "", sourceType: "document", description: "", uri: "" })}
              disabled={isCreating}
            >
              Reset form
            </button>
            <button type="button" className="button button-ghost" onClick={() => void loadSources()} disabled={loadingSources}>
              {loadingSources ? "Refreshing…" : "Refresh sources"}
            </button>
          </div>

          <div aria-live="polite">{error ? <InlineAlert tone="danger" title={error} /> : null}</div>
        </form>
      </Card>

      <Card title="Specification sources">
        {loadingSources ? (
          <p className="text-sm text-slate-600">Loading sources…</p>
        ) : sources.length === 0 ? (
          <p className="text-sm text-slate-600">No spec sources yet. Add one to start analyzing.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-slate-200">
            {sources.map((source) => {
              const badge = getBadgeConfig(source.id, analyzingId, sectionsBySource);

              return (
                <li key={source.id} className="flex flex-col gap-2 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-slate-800">{source.name}</span>
                        <Badge label={badge.label} variant={badge.variant} />
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                        <span className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold uppercase tracking-wide">
                          {source.sourceType}
                        </span>
                        {badge.description ? <span>{badge.description}</span> : null}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="button button-primary"
                        disabled={analyzingId === source.id}
                        onClick={() => void handleAnalyze(source.id)}
                      >
                        {analyzingId === source.id ? "Analyzing…" : "Analyze with AI"}
                      </button>
                    </div>
                  </div>

                  {source.description ? (
                    <p className="text-sm text-slate-600">{source.description}</p>
                  ) : null}

                  {source.uri ? (
                    <a
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                      href={source.uri}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {source.uri}
                    </a>
                  ) : null}

                  <div className="flex flex-col gap-2 rounded border border-slate-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-800">Attachments</span>
                      {loadingDocuments ? (
                        <span className="text-xs text-slate-500">Refreshing documents…</span>
                      ) : null}
                    </div>

                    {(() => {
                      const attached = [...(documentsBySource.get(source.id) ?? [])];
                      if (source.documentId) {
                        const doc = documentsById.get(source.documentId);
                        if (doc && !attached.find((item) => item.id === doc.id)) {
                          attached.push(doc);
                        }
                      }

                      if (attached.length === 0) {
                        return <p className="text-sm text-slate-600">No documents attached yet.</p>;
                      }

                      return (
                        <ul className="flex flex-col gap-2">
                          {attached.map((doc) => (
                            <li key={doc.id} className="rounded border border-slate-100 p-2 text-sm text-slate-700">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="font-medium text-slate-800">{doc.title ?? doc.filename}</span>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <a
                                    href={`/api/documents/${encodeURIComponent(doc.id)}/file`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-indigo-600 hover:text-indigo-700"
                                  >
                                    View
                                  </a>
                                  <span>
                                    {doc.mimeType} · {(doc.size / 1024).toFixed(1)} KB
                                  </span>
                                </div>
                              </div>
                          <div className="text-xs text-slate-600">
                            Text extraction: {doc.textExtraction?.status ?? "pending"}
                          </div>
                        </li>
                      ))}
                    </ul>
                      );
                    })()}

                    {projectId ? (
                      <label className="flex flex-col gap-1 text-sm text-slate-700">
                        <span>Upload and attach document</span>
                        <input
                          type="file"
                          className="input"
                          disabled={uploadingSourceId === source.id}
                          onChange={(event: any) => {
                            const file = event.target.files?.[0] ?? null;
                            void handleUpload(source.id, file);
                            event.target.value = "";
                          }}
                        />
                      </label>
                    ) : (
                      <p className="text-xs text-slate-500">No project selected — cannot upload documents.</p>
                    )}

                    <p className="text-xs text-slate-500">
                      Analyze with AI will prefer the attached document text when available.
                    </p>

                    {uploadingSourceId === source.id ? (
                      <InlineAlert tone="info" title="Uploading" description="Linking document to source…" />
                    ) : null}
                    {uploadError ? (
                      <InlineAlert tone="danger" title="Upload failed" description={uploadError} />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card title="Generated sections">
        {loadingSections ? (
          <p className="text-sm text-slate-600">Loading sections…</p>
        ) : sections.length === 0 ? (
          <p className="text-sm text-slate-600">No sections generated yet. Analyze a source to populate this area.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {sectionGroups.map((group) => {
              const sourceSections = group.sections;

              return (
                <div key={group.id} className="rounded border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-800">{group.label}</h4>
                    <span className="text-xs text-slate-500">{sourceSections.length} sections</span>
                  </div>
                  {sourceSections.length === 0 ? (
                    <p className="text-sm text-slate-600">No sections generated for this source yet.</p>
                  ) : (
                    <ol className="mt-2 list-decimal space-y-2 pl-5">
                      {sourceSections.map((section) => (
                        <li key={section.id} className="text-sm text-slate-700">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 font-semibold text-slate-800">
                                <span>{section.title}</span>
                                <AiReviewStatusBadge status={section.aiReviewStatus} />
                              </div>
                              <div className="whitespace-pre-line text-slate-700">{section.content}</div>

                              <details className="mt-2 rounded border border-slate-200 bg-slate-50 p-2" open>
                                <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-700">
                                  DSL bridge
                                  <span className="rounded bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                    {section.dslKind ?? "Unspecified"}
                                  </span>
                                  <span className="rounded bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                                    {section.dslStatus ?? "NOT_STARTED"}
                                  </span>
                                </summary>

                                <div className="mt-2 space-y-2 text-xs text-slate-700">
                                  {section.dslAccepted ? (
                                    <div className="space-y-1">
                                      <div className="font-semibold text-slate-800">Accepted fragment</div>
                                      <pre className="overflow-x-auto rounded bg-white p-2 text-[11px] text-slate-800">
                                        {JSON.stringify(section.dslAccepted, null, 2)}
                                      </pre>
                                    </div>
                                  ) : (
                                    <p className="text-slate-600">No accepted DSL fragment yet.</p>
                                  )}

                                  {section.dslCandidates ? (
                                    <div className="space-y-1">
                                      <div className="font-semibold text-slate-800">Candidates</div>
                                      <p className="text-slate-700">
                                        {Array.isArray(section.dslCandidates)
                                          ? `${section.dslCandidates.length} candidate${section.dslCandidates.length === 1 ? "" : "s"} available.`
                                          : "Candidate data available."}
                                      </p>
                                      {typeof section.dslCandidates === "object" ? (
                                        <pre className="overflow-x-auto rounded bg-white p-2 text-[11px] text-slate-800">
                                          {JSON.stringify(section.dslCandidates, null, 2)}
                                        </pre>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <p className="text-slate-600">No DSL candidates captured.</p>
                                  )}
                                </div>
                              </details>
                            </div>
                            <button
                              type="button"
                              className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                              disabled={deletingSectionId === section.id}
                              onClick={() => void handleDeleteSection(section.id)}
                            >
                              {deletingSectionId === section.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
